import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Badge, Box, Flex, Tabs, Text } from "@mantine/core";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import {
  EXPLORE_TAB_SUBTITLES,
  exploreTabQueryValue,
  parseExploreTabParam,
  type ExploreTab,
} from "@/pages/discover/discoverExplore.const";
import {
  NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
  NAV_TABS_FULL_WIDTH_LINE_STYLE,
  navyNavTabSegmentBorders,
  navyProfileNavTabBorderRadius,
} from "@/shared/styles/navTabsLayout";
import { useNavTabsStackLayout } from "@/shared/styles/useNavTabsStackLayout";
import classes from "./DiscoverExploreTabs.module.css";

const TAB_ORDER: ExploreTab[] = ["companies", "experts", "corridors", "expos"];

const TAB_LABELS: Record<ExploreTab, string> = {
  companies: "Companies",
  experts: "Regional and Sector Experts",
  corridors: "Clusters and Corridors",
  expos: "Global Events and Exhibitions",
};

const TAB_BADGE_LABEL: Partial<Record<ExploreTab, string>> = {
  experts: "New",
  corridors: "New",
  expos: "New",
};

function tabBadgeLabel(tab: ExploreTab, compact: boolean): string | undefined {
  const label = TAB_BADGE_LABEL[tab];
  if (!label) return undefined;
  if (compact && label === "Coming Soon") return "Soon";
  return label;
}

const TAB_NAVY = "#04305c";
const TAB_YELLOW = "#e6ac00";
/** Peek of neighboring header tabs when auto-scrolling the active tab into view (mobile). */
const HEADER_TAB_PEEK_PX = 32;
const HEADER_PILL_TRANSITION = {
  type: "spring" as const,
  stiffness: 240,
  damping: 30,
  mass: 0.95,
};

function ExploreTabBadge({
  label,
  compact,
  onYellowTab,
}: {
  label: string;
  compact?: boolean;
  onYellowTab?: boolean;
}) {
  const isLong = label.length > 6;
  return (
    <Badge
      size={compact ? "xs" : "sm"}
      radius="xl"
      variant="filled"
      px={compact ? 4 : 8}
      h={compact ? 14 : 20}
      styles={{
        root: {
          background: onYellowTab
            ? TAB_NAVY
            : "linear-gradient(180deg, #fde047 0%, #facc15 100%)",
          color: onYellowTab ? "#ffffff" : "#1c1917",
          fontWeight: 800,
          letterSpacing: isLong
            ? compact
              ? "0.01em"
              : "0.03em"
            : compact
              ? "0.02em"
              : "0.06em",
          textTransform: "uppercase",
          fontSize: isLong ? (compact ? 7 : 9) : compact ? 8 : 10,
          border: onYellowTab ? `1px solid ${TAB_NAVY}` : "1px solid rgba(234, 179, 8, 0.5)",
          boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
          flexShrink: 0,
        },
      }}
    >
      {label}
    </Badge>
  );
}

type DiscoverExploreTabsProps = {
  activeTab: ExploreTab | null;
  onTabChange: (tab: ExploreTab) => void;
  placement?: "page" | "header";
};

export function useExploreTabState() {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = useMemo(
    () => parseExploreTabParam(searchParams.get("tab")),
    [searchParams],
  );

  const setActiveTab = useCallback(
    (tab: ExploreTab) => {
      const next = new URLSearchParams(searchParams);
      if (tab === "companies") {
        next.delete("tab");
      } else {
        const query = exploreTabQueryValue(tab);
        if (query) next.set("tab", query);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return { activeTab, setActiveTab };
}

function tabSlot(index: number, total: number): "first" | "middle" | "last" {
  if (index === 0) return "first";
  if (index === total - 1) return "last";
  return "middle";
}

export default function DiscoverExploreTabs({
  activeTab,
  onTabChange,
  placement = "page",
}: DiscoverExploreTabsProps) {
  const isHeader = placement === "header";
  const {
    belowSm,
    tabsListStyles: navTabsListStyles,
    tabsRootStyles: navTabsRootStyles,
  } = useNavTabsStackLayout(
    TAB_ORDER.length,
    {
      gridTemplateColumns: isHeader
        ? `repeat(${TAB_ORDER.length}, minmax(0, 1fr))`
        : `repeat(${TAB_ORDER.length}, minmax(max-content, 1fr))`,
      alignItems: "stretch",
      width: "100%",
      maxWidth: "100%",
      ...(isHeader
        ? {
            gap: 0,
            overflow: "visible" as const,
            display: "grid" as const,
          }
        : {}),
    },
    { equalColumnsOnMobile: true },
  );

  // `isCompactTabNav` stays false for this 4-tab row; use viewport width for compact chrome.
  const isCompactTabs = belowSm === true;
  const headerScrollable = isHeader && isCompactTabs;
  const useDenseChrome = isHeader || isCompactTabs;

  const tabsListStyles = {
    list: {
      ...navTabsListStyles.list,
      ...(headerScrollable
        ? {
            display: "inline-flex" as const,
            flexWrap: "nowrap" as const,
            width: "max-content" as const,
            maxWidth: "none" as const,
            gap: 0,
          }
        : {}),
      ["--tabs-list-border-color" as string]: "transparent",
      ["--tabs-list-border-width" as string]: "0px",
    },
  };

  const trackRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [tabRects, setTabRects] = useState<Partial<Record<ExploreTab, { left: number; width: number }>>>({});

  const measureHeaderTabs = useCallback(() => {
    const track = trackRef.current;
    if (!isHeader || !track) return;
    const trackBox = track.getBoundingClientRect();
    const next: Partial<Record<ExploreTab, { left: number; width: number }>> = {};
    for (const tab of TAB_ORDER) {
      const tabEl = track.querySelector(`[data-explore-tab="${tab}"]`);
      if (!(tabEl instanceof HTMLElement)) continue;
      const tabBox = tabEl.getBoundingClientRect();
      next[tab] = {
        left: tabBox.left - trackBox.left + track.scrollLeft,
        width: tabBox.width,
      };
    }
    setTabRects((prev) => {
      for (const tab of TAB_ORDER) {
        const a = prev[tab];
        const b = next[tab];
        if (!a || !b || Math.abs(a.left - b.left) > 0.5 || Math.abs(a.width - b.width) > 0.5) {
          return next;
        }
      }
      return prev;
    });
  }, [isHeader]);

  const scrollActiveHeaderTabIntoView = useCallback(() => {
    if (!headerScrollable || !activeTab) return;
    const scroller = scrollRef.current;
    const track = trackRef.current;
    const tabEl = track?.querySelector(`[data-explore-tab="${activeTab}"]`);
    if (!scroller || !(tabEl instanceof HTMLElement)) return;

    const scrollerWidth = scroller.clientWidth;
    const maxScroll = Math.max(0, scroller.scrollWidth - scrollerWidth);
    const tabLeft = tabEl.offsetLeft;
    const tabRight = tabLeft + tabEl.offsetWidth;
    const activeIndex = TAB_ORDER.indexOf(activeTab);
    const isFirst = activeIndex <= 0;
    const isLast = activeIndex >= TAB_ORDER.length - 1;
    const peek = HEADER_TAB_PEEK_PX;

    let nextScroll: number;

    if (isFirst) {
      // Start of track — active fully visible; right neighbors peek naturally.
      nextScroll = 0;
    } else if (isLast) {
      // End of track — active fully visible; left neighbors peek.
      nextScroll = maxScroll;
    } else {
      // Middle tabs: keep active fully visible with peeks on both sides when space allows.
      nextScroll = Math.max(0, tabLeft - peek);
      const needsRightPeek = tabRight + peek > nextScroll + scrollerWidth;
      if (needsRightPeek) {
        nextScroll = Math.max(0, tabRight + peek - scrollerWidth);
      }
      // Guarantee the active tab itself is fully inside the viewport.
      if (tabLeft < nextScroll) nextScroll = tabLeft;
      if (tabRight > nextScroll + scrollerWidth) {
        nextScroll = Math.max(0, tabRight - scrollerWidth);
      }
    }

    scroller.scrollLeft = Math.min(maxScroll, Math.max(0, nextScroll));
  }, [activeTab, headerScrollable]);

  useLayoutEffect(() => {
    measureHeaderTabs();
    scrollActiveHeaderTabIntoView();
  }, [measureHeaderTabs, scrollActiveHeaderTabIntoView]);

  useEffect(() => {
    if (!isHeader) return;
    const track = trackRef.current;
    if (!track) return;
    const observer = new ResizeObserver(() => {
      measureHeaderTabs();
      scrollActiveHeaderTabIntoView();
    });
    observer.observe(track);
    const scroller = scrollRef.current;
    if (scroller) observer.observe(scroller);
    window.addEventListener("resize", measureHeaderTabs);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measureHeaderTabs);
    };
  }, [isHeader, measureHeaderTabs, scrollActiveHeaderTabIntoView]);

  const headerPill = activeTab ? tabRects[activeTab] : undefined;

  const tabStyle = (tab: ExploreTab, slot: "first" | "middle" | "last") => {
    const isActive = activeTab === tab;
    const tabIndex = TAB_ORDER.indexOf(tab);
    const nextTab = TAB_ORDER[tabIndex + 1];
    const showDivider =
      isHeader &&
      tabIndex >= 0 &&
      tabIndex < TAB_ORDER.length - 1 &&
      tab !== activeTab &&
      nextTab !== activeTab;
    return {
      ...NAV_TAB_EQUAL_WIDTH_CELL_STYLE,
      height: "100%",
      alignSelf: "stretch" as const,
      backgroundColor: isHeader ? "transparent" : isActive ? TAB_NAVY : TAB_YELLOW,
      color: isHeader ? TAB_NAVY : isActive ? "#ffffff" : TAB_NAVY,
      borderRadius: isHeader ? 999 : navyProfileNavTabBorderRadius(false, slot),
      ...(isHeader
        ? {
            border: "none",
            borderRight: "1px solid",
            borderRightColor: showDivider ? "#E2E8F0" : "transparent",
            background: "transparent",
            boxShadow: "none",
            boxSizing: "border-box" as const,
            height: 40,
            position: "relative" as const,
            zIndex: 1,
          }
        : navyNavTabSegmentBorders(isActive)),
      fontWeight: isHeader ? 600 : isActive ? 700 : 600,
      cursor: "pointer" as const,
      minHeight: isCompactTabs ? 44 : isHeader ? 40 : 48,
      width: headerScrollable ? "max-content" : isHeader ? "100%" : undefined,
      minWidth: headerScrollable ? "max-content" : 0,
      maxWidth: headerScrollable ? "none" : "100%",
      flexShrink: headerScrollable ? 0 : undefined,
      whiteSpace: "nowrap" as const,
      overflow: isHeader && !headerScrollable ? ("hidden" as const) : ("visible" as const),
      textOverflow: isHeader && !headerScrollable ? ("ellipsis" as const) : undefined,
      paddingInline: isHeader ? (useDenseChrome ? 8 : 12) : useDenseChrome ? 2 : undefined,
      paddingBlock: isHeader ? 4 : useDenseChrome ? 4 : undefined,
      fontSize: isCompactTabs ? 11 : isHeader ? 14 : undefined,
    };
  };

  return (
    <Box
      w={isHeader ? undefined : "100%"}
      mb={0}
      pt={0}
      miw={0}
      style={
        isHeader
          ? {
              flex: "1 1 auto",
              minWidth: 0,
              height: "100%",
              display: "flex",
              width: "100%",
              maxWidth: "100%",
            }
          : undefined
      }
    >
      <Tabs
        value={activeTab}
        onChange={(value) => {
          if (value) onTabChange(parseExploreTabParam(value));
        }}
        styles={{
          ...navTabsRootStyles,
          root: isHeader
            ? {
                width: "100%",
                maxWidth: "100%",
                height: "100%",
                minWidth: 0,
                flex: 1,
              }
            : undefined,
          tab: {
            ...navTabsRootStyles.tab,
            overflow: "visible",
            minWidth: 0,
            ...(isHeader
              ? {
                  zIndex: 1,
                  borderRadius: 999,
                  backgroundColor: "transparent",
                  border: "none",
                  position: "relative",
                }
              : {}),
          },
          tabLabel: {
            overflow: "visible",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          },
        }}
      >
        <Box
          ref={isHeader ? scrollRef : undefined}
          w={isHeader ? undefined : "100%"}
          className={headerScrollable ? classes.headerTrackScroll : undefined}
          style={
            isHeader
              ? {
                  height: 48,
                  width: "100%",
                  maxWidth: "100%",
                  background: "#F1F5F9",
                  borderRadius: 9999,
                  boxSizing: "border-box",
                  overflow: headerScrollable ? undefined : "hidden",
                }
              : NAV_TABS_FULL_WIDTH_LINE_STYLE
          }
        >
          {isHeader ? (
            <Box
              ref={trackRef}
              style={{
                position: "relative",
                width: headerScrollable ? "max-content" : "100%",
                minWidth: headerScrollable ? "100%" : "100%",
                height: "100%",
                padding: 4,
                boxSizing: "border-box",
              }}
            >
              <motion.div
                aria-hidden
                initial={false}
                animate={{
                  x: headerPill?.left ?? 0,
                  width: headerPill?.width ?? 0,
                  opacity: headerPill ? 1 : 0,
                }}
                transition={{
                  x: HEADER_PILL_TRANSITION,
                  width: HEADER_PILL_TRANSITION,
                  opacity: { duration: 0.18 },
                }}
                style={{
                  position: "absolute",
                  top: 4,
                  left: 0,
                  height: 40,
                  borderRadius: 999,
                  background: TAB_YELLOW,
                  zIndex: 0,
                  pointerEvents: "none",
                }}
              />
              <Tabs.List className={classes.hideListBefore} styles={tabsListStyles}>
                {TAB_ORDER.map((tab, index) => {
                  const badgeLabel = tabBadgeLabel(tab, useDenseChrome);
                  return (
                    <Tabs.Tab
                      key={tab}
                      value={tab}
                      data-explore-tab={tab}
                      px={{ base: 4, sm: 12 }}
                      py={{ base: 4, sm: 4 }}
                      fz={{ base: 11, sm: 14 }}
                      style={tabStyle(tab, tabSlot(index, TAB_ORDER.length))}
                    >
                      <Flex
                        align="center"
                        justify="center"
                        gap={6}
                        direction="row"
                        wrap="nowrap"
                        w="100%"
                        h="100%"
                        miw={0}
                        pos="relative"
                        style={{ zIndex: 1 }}
                        c={TAB_NAVY}
                      >
                        <Text
                          component="span"
                          c={TAB_NAVY}
                          fw="inherit"
                          fz="inherit"
                          lh={1.15}
                          ta="center"
                          style={{
                            whiteSpace: "nowrap",
                            overflowWrap: "normal",
                            overflow: isHeader && !headerScrollable ? "hidden" : undefined,
                            textOverflow: isHeader && !headerScrollable ? "ellipsis" : undefined,
                            flexShrink: isHeader && !headerScrollable ? 1 : 0,
                            minWidth: 0,
                          }}
                        >
                          {TAB_LABELS[tab]}
                        </Text>
                        {badgeLabel ? (
                          <ExploreTabBadge
                            label={badgeLabel}
                            compact
                            onYellowTab
                          />
                        ) : isCompactTabs ? (
                          <Box h={14} aria-hidden style={{ visibility: "hidden", flexShrink: 0 }} />
                        ) : null}
                      </Flex>
                    </Tabs.Tab>
                  );
                })}
              </Tabs.List>
            </Box>
          ) : (
            <Tabs.List className={classes.hideListBefore} styles={tabsListStyles}>
              {TAB_ORDER.map((tab, index) => {
                const isActive = activeTab === tab;
                const badgeLabel = tabBadgeLabel(tab, useDenseChrome);
                return (
                <Tabs.Tab
                  key={tab}
                  value={tab}
                  px={{ base: 4, sm: "xl" }}
                  py={{ base: 4, sm: "sm" }}
                  fz={{ base: 11, sm: "lg" }}
                  style={tabStyle(tab, tabSlot(index, TAB_ORDER.length))}
                >
                  <Flex
                    align="center"
                    justify="center"
                    gap={isCompactTabs ? 4 : 8}
                    direction={isCompactTabs ? "column" : "row"}
                    wrap="nowrap"
                    w="100%"
                    h="100%"
                    miw={0}
                    pos="relative"
                    style={{ zIndex: 1 }}
                    c={!isActive ? TAB_NAVY : "#ffffff"}
                  >
                    <Text
                      component="span"
                      c={!isActive ? TAB_NAVY : "#ffffff"}
                      fw="inherit"
                      fz="inherit"
                      lh={1.15}
                      ta="center"
                      style={{
                        whiteSpace: "normal",
                        overflowWrap: "break-word",
                        minWidth: 0,
                      }}
                    >
                      {TAB_LABELS[tab]}
                    </Text>
                    {badgeLabel ? (
                      <ExploreTabBadge
                        label={badgeLabel}
                        compact={useDenseChrome}
                        onYellowTab={!isActive}
                      />
                    ) : isCompactTabs ? (
                      <Box h={14} aria-hidden style={{ visibility: "hidden", flexShrink: 0 }} />
                    ) : null}
                  </Flex>
                </Tabs.Tab>
              );
              })}
            </Tabs.List>
          )}
        </Box>

        {!isHeader && activeTab && activeTab !== "companies" ? (
          <Text size="sm" c="dimmed" mt="xs" mb="xs">
            {EXPLORE_TAB_SUBTITLES[activeTab]}
          </Text>
        ) : null}
      </Tabs>
    </Box>
  );
}
